// シフト決定（リスト表示）の「未確定のコースを表示」ボタン
// - 右（次へ）を押すと、一覧の中のメイン講師未決定の行を先頭から順に表示する
//   （未選択なら最初の行、末尾まで来たら先頭に戻る）
// - 左（前へ）を押すと、逆順に表示する（未選択なら最後の行、先頭まで来たら末尾に戻る）
// - 表示するときは一覧のスクロール領域（.js-scroll-to-undecided-container）の中だけをスクロールしてその行を中央に出し、
//   一定時間ハイライトしてメイン講師のセレクトにフォーカスを当てる（ページ全体はスクロールしないので、ボタン類の位置は動かない）
// - 表頭のフィルターで非表示（display: none）になっている行は飛ばす
// - 未決定の行が無い場合はボタンの隣にメッセージを表示する
//
// 必要な DOM（index.blade.php）:
//   .js-scroll-to-undecided                ボタングループ
//   .js-scroll-to-undecided-prev / -next   前へ・次へボタン
//   .js-scroll-to-undecided-message        未決定の行が無いときのメッセージ（hidden 属性で切り替え）
//   .js-scroll-to-undecided-container      一覧のスクロール領域（overflow-y: auto の要素。行の祖先）
//   tr[data-main-lecturer-undecided="1"]   メイン講師未決定の行

const GROUP_SELECTOR = '.js-scroll-to-undecided'
const PREV_SELECTOR = '.js-scroll-to-undecided-prev'
const NEXT_SELECTOR = '.js-scroll-to-undecided-next'
const MESSAGE_SELECTOR = '.js-scroll-to-undecided-message'
const CONTAINER_SELECTOR = '.js-scroll-to-undecided-container'
const UNDECIDED_ROW_SELECTOR = 'tr[data-main-lecturer-undecided="1"]'

/** ハイライトを消すまでの時間（ミリ秒） */
const HIGHLIGHT_MS = 2000
/** メッセージを消すまでの時間（ミリ秒） */
const MESSAGE_MS = 3000
const HIGHLIGHT_OUTLINE = '3px solid #4f46e5'

/**
 * フィルターで隠されていない行か
 * @param {HTMLElement} row
 * @returns {boolean}
 */
function isVisibleRow(row) {
    return !row.hidden && row.style.display !== 'none'
}

/**
 * 表示中のメイン講師未決定の行（文書順）
 * @returns {HTMLElement[]}
 */
function listVisibleUndecidedRows() {
    return Array.from(document.querySelectorAll(UNDECIDED_ROW_SELECTOR)).filter(isVisibleRow)
}

/**
 * 現在の行を基準に、前後どちらかの未決定行を選ぶ（端では反対側へ回り込む）
 * - 現在の行が無い（未選択）とき: 次へなら先頭、前へなら末尾
 * - 現在の行がフィルターで隠れたとき: 文書順で次へなら後ろ、前へなら手前にある最初の表示中の行
 * @param {HTMLElement[]} rows 表示中の未決定行
 * @param {HTMLElement|null} current 現在ハイライト中の行
 * @param {number} direction 1 = 次へ, -1 = 前へ
 * @returns {HTMLElement|null}
 */
function pickRow(rows, current, direction) {
    if (rows.length === 0) {
        return null
    }
    if (!current) {
        return direction > 0 ? rows[0] : rows[rows.length - 1]
    }

    const index = rows.indexOf(current)
    if (index >= 0) {
        return rows[(index + direction + rows.length) % rows.length]
    }

    // 現在の行が非表示になっている場合は文書順で前後を探す
    const following = current.compareDocumentPosition.bind(current)
    const candidates = direction > 0
        ? rows.filter((row) => following(row) & Node.DOCUMENT_POSITION_FOLLOWING)
        : rows.filter((row) => following(row) & Node.DOCUMENT_POSITION_PRECEDING)
    if (candidates.length > 0) {
        return direction > 0 ? candidates[0] : candidates[candidates.length - 1]
    }

    return direction > 0 ? rows[0] : rows[rows.length - 1]
}

/**
 * 一覧のスクロール領域の中だけをスクロールして、行を（固定表頭の下の）表示範囲の中央に出す
 * ページ全体や他の祖先はスクロールしない（scrollIntoView は祖先すべてをスクロールしてボタン類の位置が動くため使わない）
 * @param {HTMLElement} container
 * @param {HTMLElement} row
 */
function scrollContainerToRow(container, row) {
    const containerRect = container.getBoundingClientRect()
    const rowRect = row.getBoundingClientRect()
    const header = container.querySelector('thead')
    const headerHeight = header ? header.getBoundingClientRect().height : 0
    // 行の上端の、スクロール領域の内容における位置
    const rowTop = container.scrollTop + (rowRect.top - containerRect.top)
    // 表頭を除いた表示範囲の中央に行が来る位置
    const visibleHeight = container.clientHeight - headerHeight
    const top = rowTop - headerHeight - (visibleHeight - rowRect.height) / 2

    if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
    } else {
        container.scrollTop = Math.max(0, top)
    }
}

/**
 * 行までスクロールしてハイライトする
 * @param {HTMLElement} row
 */
function revealRow(row) {
    const container = row.closest(CONTAINER_SELECTOR)
    if (container) {
        scrollContainerToRow(container, row)
    } else if (typeof row.scrollIntoView === 'function') {
        // スクロール領域が無い（想定外のマークアップ）ときだけ従来どおり
        row.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }

    row.style.outline = HIGHLIGHT_OUTLINE
    row.style.outlineOffset = '-3px'
    window.setTimeout(() => {
        row.style.outline = ''
        row.style.outlineOffset = ''
    }, HIGHLIGHT_MS)

    const select = row.querySelector('select')
    if (select) {
        select.focus({ preventScroll: true })
    }
}

/**
 * 未決定の行が無いときのメッセージを一定時間表示する
 * @param {HTMLElement|null} message
 */
function showNotFoundMessage(message) {
    if (!message) {
        return
    }
    message.hidden = false
    window.setTimeout(() => {
        message.hidden = true
    }, MESSAGE_MS)
}

/**
 * ボタンを探してクリックを設定する（一覧を描き直したあとにも呼ぶ。現在行は呼ぶたびにリセットする）
 *
 * @returns {void}
 */
export const setUpScrollToUndecided = () => {
    const group = document.querySelector(GROUP_SELECTOR)
    if (!group || group.dataset.scrollToUndecidedReady === '1') {
        return
    }
    group.dataset.scrollToUndecidedReady = '1'
    const message = document.querySelector(MESSAGE_SELECTOR)
    /** 現在ハイライト中の行（次へ・前への基準） */
    let current = null

    const move = (direction) => (event) => {
        event.preventDefault()
        const row = pickRow(listVisibleUndecidedRows(), current, direction)
        if (row) {
            current = row
            revealRow(row)
        } else {
            current = null
            showNotFoundMessage(message)
        }
    }

    const prev = group.querySelector(PREV_SELECTOR)
    const next = group.querySelector(NEXT_SELECTOR)
    if (prev) {
        prev.addEventListener('click', move(-1))
    }
    if (next) {
        next.addEventListener('click', move(1))
    }
}

document.addEventListener('DOMContentLoaded', setUpScrollToUndecided)
